import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@contexts/User.context';
import { getAnnotationSummary, getTaxonomyNodesForMode } from '@utils/api';
import { buildObjectTree } from '@utils/taxonomyTree';
import { mergeAnnotationSummary } from '@routes/userRoutes/objects/objectTree.js';

/**
 * The caller's taxonomy for one project type: the flat `nodes` list and the same
 * nodes as a nested `tree`. Cached per mode, so every consumer shares one request.
 * The request sends only the mode — the server reads the user from the token and
 * the active org from its own state — so both go in the key, or an org switch
 * would keep serving the previous org's cached tree.
 *
 * @param {string} mode - A `PROJECT_MODE` value.
 */
export const useObjects = mode => {
	const { currentUser } = useAuth();
	const query = useQuery({
		queryKey: ['objectTaxonomy', currentUser?.uuid, currentUser?.active_org_id, { mode }],
		queryFn: () => getTaxonomyNodesForMode(mode),
		enabled: Boolean(mode),
		// The taxonomy changes rarely; without this the map refetches it on every
		// panel mount and window focus.
		staleTime: 5 * 60 * 1000,
	});

	// Separate from the tree: it can lag behind or fail without hiding the tree,
	// and the cards show a placeholder until it arrives.
	const summaryQuery = useQuery({
		queryKey: ['annotationSummary', currentUser?.uuid, currentUser?.active_org_id],
		queryFn: getAnnotationSummary,
	});

	// Stable while loading, so the tree is not rebuilt on every render.
	const nodes = useMemo(
		() => mergeAnnotationSummary(query.data ?? [], summaryQuery.data ?? []),
		[query.data, summaryQuery.data]
	);
	const tree = useMemo(() => buildObjectTree(nodes), [nodes]);

	return { nodes, tree, isLoading: query.isLoading, isError: query.isError };
};
